import { z } from "zod";

/**
 * Display-only view of the access-token claims (DEC-006 / PLAN §7). The signature is verified
 * by the API on every request; the client only reads `exp` and the impersonation markers.
 */
const claimsSchema = z
  .object({
    sub: z.string().optional(),
    aud: z.union([z.string(), z.array(z.string())]).optional(),
    exp: z.number().optional(),
    /** Impersonation session id (admin "View as tenant"). */
    imp: z.string().optional(),
    /** Workspace (tenant) id bound to an impersonation token. */
    ws: z.string().optional(),
    /** Read-only impersonation. */
    ro: z.boolean().optional(),
  })
  .passthrough();

export type AccessClaims = z.infer<typeof claimsSchema>;

function base64UrlDecode(segment: string): string {
  const padded = segment.replace(/-/g, "+").replace(/_/g, "/").padEnd(Math.ceil(segment.length / 4) * 4, "=");
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/** Returns the payload claims, or null for anything that is not a well-formed JWT. */
export function readClaims(token: string): AccessClaims | null {
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const parsed = claimsSchema.safeParse(JSON.parse(base64UrlDecode(payload)));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export interface AccessToken {
  value: string;
  /** Epoch ms; from `expires_in` when the API sent it, else from the `exp` claim. */
  expiresAt: number;
  claims: AccessClaims | null;
}

const FALLBACK_TTL_MS = 60_000;

export function toAccessToken(value: string, expiresInSeconds: number | null | undefined, now: number): AccessToken {
  const claims = readClaims(value);
  const fromClaim = claims?.exp ? claims.exp * 1000 : undefined;
  const fromBody = typeof expiresInSeconds === "number" ? now + expiresInSeconds * 1000 : undefined;
  return { value, expiresAt: fromBody ?? fromClaim ?? now + FALLBACK_TTL_MS, claims };
}
